import React from "react";
import { observer, inject } from "mobx-react";
import { observable } from "mobx";
import CreateIcon from "@material-ui/icons/Create";
import SimpleModal from "@src/Components/Common/SimpleModal/SimpleModal";
import { IManagementStore } from "Stores/Management/types";
import { Values } from "Tools/Values";
import { Theme } from "@src/Interfaces/Entity/Theme";
import { IFile } from "@src/Interfaces/Entity/File";
import ThemeForm from "../../../ThemeForm/ThemeForm";
import { IThemesStore } from "@src/Stores/Themes/types";
import { IValuesStore } from "@src/Stores/Values/types";
import RoundButton from "@src/Shared/Components/RoundButton/RoundButton";

interface IProps {
  management?: IManagementStore;
  values?: IValuesStore;
  theme: Theme;
  themes?: IThemesStore;
}

@inject("management", "values", "themes")
@observer
export default class ThemeEdit extends React.Component<IProps> {
  @observable editOpened: boolean = false;
  private originalThemeState: number;

  handleOpenEdit = () => {
    this.editOpened = true;
    this.originalThemeState = this.props.theme.state;
  };

  handleCloseEdit = () => {
    this.editOpened = false;
  };

  handleResetOrUploadImage = async (image: File, imageId: string, objectId: string, fileType: number) => {
    if (!this.isImageChanged(image)) {
      return imageId;
    }
    if (image.name === "reset") {
      return null;
    }
    const file: IFile = await this.props.management.uploadFile(image, objectId, fileType);
    return file.id;
  };

  handleImageChanged = async (theme: Theme) => {
    const {
      ApplicationLogoImage,
      LoginBackgroundLandscapeImage,
      LoginBackgroundPortraitImage,
      LoginLogoImage,
    } = this.props.values.fileTypes;
    const isApplicationTheme = theme.type == this.props.values.themeTypes.Application;
    if (!isApplicationTheme) {
      theme.loginThemeDetails.loginBackgroundLandscapeImageId = await this.handleResetOrUploadImage(
        theme.loginThemeDetails.loginBackgroundLandscapeImage,
        theme.loginThemeDetails.loginBackgroundLandscapeImageId,
        theme.id,
        LoginBackgroundLandscapeImage
      );
      theme.loginThemeDetails.loginBackgroundPortraitImageId = await this.handleResetOrUploadImage(
        theme.loginThemeDetails.loginBackgroundPortraitImage,
        theme.loginThemeDetails.loginBackgroundPortraitImageId,
        theme.id,
        LoginBackgroundPortraitImage
      );
      theme.loginThemeDetails.loginLogoImageId = await this.handleResetOrUploadImage(
        theme.loginThemeDetails.loginLogoImage,
        theme.loginThemeDetails.loginLogoImageId,
        theme.id,
        LoginLogoImage
      );
    } else {
      theme.applicationThemeDetails.applicationLogoImageId = await this.handleResetOrUploadImage(
        theme.applicationThemeDetails.applicationLogoImage,
        theme.applicationThemeDetails.applicationLogoImageId,
        theme.id,
        ApplicationLogoImage
      );
    }
    return theme;
  };

  isImageChanged = (image: File) => !(image.name === "default" && image.size === 0);

  handleUpdate = async (values: Theme) => {
    try {
      let isAnyImageChanged = false;
      const isApplicationTheme = values.type == this.props.values.themeTypes.Application;
      if (isApplicationTheme) {
        isAnyImageChanged = this.isImageChanged(values.applicationThemeDetails.applicationLogoImage);
      } else {
        isAnyImageChanged =
          this.isImageChanged(values.loginThemeDetails.loginBackgroundLandscapeImage) ||
          this.isImageChanged(values.loginThemeDetails.loginBackgroundPortraitImage) ||
          this.isImageChanged(values.loginThemeDetails.loginLogoImage);
      }
      if (isAnyImageChanged) {
        values = await this.handleImageChanged(values);
      }
      let updatedTheme = await this.props.management.saveTheme(values);
      if (
        isApplicationTheme &&
        (updatedTheme.state === this.props.values.states.Active ||
          this.originalThemeState === this.props.values.states.Active)
      ) {
        await this.props.themes.getCurrentTheme(this.props.values.themeTypes.Application);
      }
      await this.props.management.getThemes();
    } catch (error) {
      console.error(error);
    }
    this.editOpened = false;
  };

  render() {
    const themeValues: Theme = {
      ...this.props.theme,
    };
    if (themeValues.type === this.props.values.themeTypes.Application) {
      themeValues.applicationThemeDetails.applicationLogoImage = new File([], "default");
    } else {
      themeValues.loginThemeDetails.loginLogoImage = new File([], "default");
      themeValues.loginThemeDetails.loginBackgroundLandscapeImage = new File([], "default");
      themeValues.loginThemeDetails.loginBackgroundPortraitImage = new File([], "default");
    }

    return (
      <React.Fragment>
        <RoundButton
            icon={<CreateIcon/>}
            tooltipTitle={Values.getLocalizationValue("edit")}
            onClick={() => this.handleOpenEdit()}
        />
        
        <SimpleModal width={500} isOpened={this.editOpened} onClose={this.handleCloseEdit}>
          <ThemeForm
            title={Values.getLocalizationValue("edit")}
            initialValues={themeValues}
            applicationLogoImageUrl={
              this.props.theme.applicationThemeDetails
                ? this.props.theme.applicationThemeDetails.applicationLogoImageUrl
                : ""
            }
            loginLogoImageUrl={
              this.props.theme.loginThemeDetails ? this.props.theme.loginThemeDetails.loginLogoImageUrl : ""
            }
            loginBackgroundLandscapeImageUrl={
              this.props.theme.loginThemeDetails
                ? this.props.theme.loginThemeDetails.loginBackgroundLandscapeImageUrl
                : ""
            }
            loginBackgroundPortraitImageUrl={
              this.props.theme.loginThemeDetails
                ? this.props.theme.loginThemeDetails.loginBackgroundPortraitImageUrl
                : ""
            }
            onSubmit={this.handleUpdate}
            onClose={this.handleCloseEdit}
            isEdit={true}
          />
        </SimpleModal>
      </React.Fragment>
    );
  }
}
